import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { AuthTransitionScreen } from "../../ui/components/AuthTransitionScreen";
import { userHydratedAtom } from "../lib/auth/atoms";

/** Give up waiting for hydrate and fall back to the dashboard rather than stranding the tab here forever. */
const HYDRATE_WAIT_TIMEOUT_MS = 3_000;

function RedirectingRoute() {
  const { redirect: target } = Route.useSearch();
  const userHydrated = useAtomValue(userHydratedAtom);

  React.useEffect(() => {
    if (!target) return; // plain chromeless placeholder — an external navigate is already underway elsewhere
    if (userHydrated) {
      window.location.replace(target);
      return;
    }
    const timeout = window.setTimeout(() => {
      window.location.replace("/");
    }, HYDRATE_WAIT_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [target, userHydrated]);

  return (
    <AuthTransitionScreen
      message={target ? "Loading…" : "Redirecting to sign in…"}
    />
  );
}

/**
 * Two uses:
 *  - Chromeless placeholder while the browser leaves for account-web (no
 *    `redirect` search param — an external location.replace is already
 *    underway elsewhere).
 *  - Wait-for-hydrate handoff: route guards send a fresh tab/deep link here
 *    with `redirect` set when the nav-permission cache hasn't warmed yet
 *    (see requireModuleAccess/requirePermission), so a valid session isn't
 *    mistaken for "no access" and bounced to the dashboard stub.
 */
export const Route = createFileRoute("/redirecting")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: RedirectingRoute,
});
